/**
 * RoadmapKanban — four status columns with drag-to-restatus.
 *
 * Within-column reordering is deliberately absent: `RoadmapItem` has no order
 * field, so a hand-placed sequence would evaporate on the next load. Cards are
 * always priority-then-recency sorted, which is why `@dnd-kit/sortable` isn't a
 * dependency — plain `useDraggable`/`useDroppable` is the whole requirement.
 *
 * The drop itself is handled by RoadmapManager: it owns the item list, so it
 * owns the optimistic update and its rollback.
 */
import { useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { RoadmapCard, DRAG_ACTIVATION_DISTANCE } from './RoadmapCard';
import { KANBAN_COLUMNS, STATUS_LABELS } from './roadmapMeta';
import { sortKanbanColumn } from './roadmapSort';
import type { RoadmapItem, RoadmapStatus } from '../../types';

interface RoadmapKanbanProps {
  items: RoadmapItem[];
  onOpen: (item: RoadmapItem) => void;
  /** Persist a new status. Resolves false when the write failed. */
  onStatusChange: (item: RoadmapItem, status: RoadmapStatus) => Promise<boolean>;
  /** Ids with a status write in flight. */
  savingIds: Set<string>;
}

export function RoadmapKanban({
  items,
  onOpen,
  onStatusChange,
  savingIds,
}: RoadmapKanbanProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE },
    }),
  );

  /**
   * Bucket by status. Items whose status is missing or outside the enum would
   * otherwise vanish from the board entirely, so they land in Backlog — visible
   * and draggable, which is how they get fixed.
   */
  const columns = useMemo(() => {
    const buckets = new Map<RoadmapStatus, RoadmapItem[]>(
      KANBAN_COLUMNS.map((status) => [status, [] as RoadmapItem[]]),
    );
    for (const item of items) {
      const bucket =
        item.status && buckets.has(item.status)
          ? buckets.get(item.status)
          : buckets.get('backlog');
      bucket?.push(item);
    }
    for (const [status, rows] of buckets) {
      buckets.set(status, sortKanbanColumn(rows));
    }
    return buckets;
  }, [items]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const target = over.id as RoadmapStatus;
    if (!KANBAN_COLUMNS.includes(target)) return;

    const item = items.find((row) => row.id === String(active.id));
    if (!item) return;
    // Dropped back where it started — nothing to persist.
    if (item.status === target) return;

    void onStatusChange(item, target);
  };

  const activeItem = activeId
    ? items.find((row) => row.id === activeId) ?? null
    : null;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {KANBAN_COLUMNS.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            rows={columns.get(status) ?? []}
            onOpen={onOpen}
            savingIds={savingIds}
            /* Don't tint the column the card already lives in. */
            dragActive={activeItem !== null && activeItem.status !== status}
          />
        ))}
      </div>
    </DndContext>
  );
}

interface KanbanColumnProps {
  status: RoadmapStatus;
  rows: RoadmapItem[];
  onOpen: (item: RoadmapItem) => void;
  savingIds: Set<string>;
  dragActive: boolean;
}

function KanbanColumn({
  status,
  rows,
  onOpen,
  savingIds,
  dragActive,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col rounded-lg border transition-colors ${
        isOver
          ? 'border-brand-400 bg-brand-50'
          : dragActive
            ? 'border-dashed border-gray-300 bg-gray-50/70'
            : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
        <span className="text-sm font-semibold text-gray-700">
          {STATUS_LABELS[status] ?? status}
        </span>
        <span className="text-xs text-gray-400">({rows.length})</span>
      </div>

      {/* Fixed max height so all four columns scroll independently. */}
      <div className="flex-1 space-y-2 p-2 overflow-y-auto min-h-[8rem] max-h-[calc(100vh-22rem)]">
        {rows.length === 0 ? (
          <p className="text-xs text-gray-400 italic text-center py-4">
            {dragActive ? 'Drop here' : 'Nothing here'}
          </p>
        ) : (
          rows.map((item) => (
            <RoadmapCard
              key={item.id}
              item={item}
              onOpen={onOpen}
              saving={savingIds.has(item.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
